import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";
import { recordActivity } from "@/services/activityLog";
import type { UserRole } from "@/store/auth";

export interface TeamMember {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  acceptedInviteAt: string | null;
}

export async function listTeamMembers(): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from("app_users")
    .select("id, email, full_name, role, is_active, created_at, accepted_invite_at")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    email: r.email,
    fullName: r.full_name,
    role: r.role as UserRole,
    isActive: r.is_active,
    createdAt: r.created_at,
    acceptedInviteAt: (r as { accepted_invite_at?: string | null }).accepted_invite_at ?? null,
  }));
}

export interface InviteAgentInput {
  email: string;
  fullName: string;
  role: UserRole;
}

export interface InviteAgentResult {
  user_id: string;
  email: string;
  invite_url: string;
  /** Whether the Resend email actually went out. False = caller should show the copy-link fallback. */
  email_sent: boolean;
  /** "fresh" for new auth users, "reinvite-pending" if they were re-invited, "reactivate" for previously deactivated. */
  reinvite_mode?: "fresh" | "reinvite-pending" | "reactivate";
  reactivated?: boolean;
}

export async function inviteAgent(input: InviteAgentInput): Promise<InviteAgentResult> {
  const { data, error } = await withTimeout(
    supabase.functions.invoke<InviteAgentResult>("invite-agent", {
      body: {
        email: input.email,
        full_name: input.fullName,
        role: input.role,
      },
    }),
    20_000,
    "Invite",
  );
  if (error) {
    const ctx = (error as { context?: { json?: () => Promise<{ error?: string; detail?: string }> } }).context;
    if (ctx?.json) {
      try {
        const body = await ctx.json();
        const msg = body?.detail ? `${body.error}: ${body.detail}` : body?.error;
        if (msg) throw new Error(msg);
      } catch (e) {
        if (e instanceof Error && e.message) throw e;
      }
    }
    throw error;
  }
  if (!data?.invite_url) throw new Error("invite-agent returned no link");
  return data;
}

export async function setMemberActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.from("app_users").update({ is_active: isActive }).eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "member_active_change",
    target_type: "app_user",
    target_id: id,
    summary: isActive ? "Re-enabled member account" : "Disabled member account",
    metadata: { is_active: isActive },
  });
}

export async function setMemberRole(id: string, role: UserRole): Promise<void> {
  const { error } = await supabase.rpc("set_member_role", { p_user_id: id, p_role: role });
  if (error) throw error;
  void recordActivity({
    kind: "member_role_change",
    target_type: "app_user",
    target_id: id,
    summary: `Changed member role to ${role}`,
    metadata: { role },
  });
}

export async function deleteMember(id: string): Promise<void> {
  const { data, error } = await withTimeout(
    supabase.functions.invoke<{ ok: boolean; error?: string; detail?: string }>(
      "delete-member",
      { body: { user_id: id } },
    ),
    20_000,
    "Delete member",
  );
  if (error) {
    const ctx = (error as { context?: { json?: () => Promise<{ error?: string; detail?: string }> } }).context;
    if (ctx?.json) {
      try {
        const body = await ctx.json();
        const msg = body?.detail ? `${body.error}: ${body.detail}` : body?.error;
        if (msg) throw new Error(msg);
      } catch (e) {
        if (e instanceof Error && e.message) throw e;
      }
    }
    throw error;
  }
  if (!data?.ok) throw new Error(data?.detail ?? data?.error ?? "Delete failed");
  // Log AFTER the target row is gone — target_id survives as a soft reference
  // (activity_log doesn't FK targets for exactly this reason).
  void recordActivity({
    kind: "member_delete",
    target_type: "app_user",
    target_id: id,
    summary: "Permanently deleted member account",
  });
}

// ---- Team settings (Organization page) -----------------------------------

export interface TeamSettings {
  id: string;
  name: string;
  description: string | null;
  working_hours_start: string;
  working_hours_end: string;
  timezone: string;
}

export async function getTeamSettings(teamId: string): Promise<TeamSettings | null> {
  const { data, error } = await supabase
    .from("teams")
    .select("id, name, description, working_hours_start, working_hours_end, timezone")
    .eq("id", teamId)
    .maybeSingle();
  if (error) throw error;
  return (data as TeamSettings | null) ?? null;
}

export async function updateTeamSettings(
  teamId: string,
  patch: Partial<Omit<TeamSettings, "id">>,
): Promise<void> {
  const { error } = await supabase.from("teams").update(patch).eq("id", teamId);
  if (error) throw error;
}

export async function setDefaultDepot(_teamId: string, depotId: string): Promise<void> {
  // Atomic RPC (migration 0034): one UPDATE flips the whole team's flags,
  // closing the clear-then-set race that could leave 0 or 2 defaults. The
  // partial unique index depots_one_default_per_team_idx backstops it.
  const { error } = await supabase.rpc("set_default_depot", { p_depot_id: depotId });
  if (error) throw error;
}
